import { z } from 'zod';

import { LAYOUT_OVERFLOW_MARGIN_MM } from './layout-v2/layout-canvas.constants';
import {
  ASSET_ROTATION_MAX_DEG,
  ASSET_ROTATION_MIN_DEG,
  GENERATED_ASSET_ID_PATTERN,
  MAX_QR_PAYLOAD_LENGTH,
  MAX_TEXT_ASSET_LENGTH,
  PREPACKAGED_LABEL_BARCODE_SOURCES,
  PREPACKAGED_LABEL_BARCODE_SYMBOLOGIES,
  PREPACKAGED_LABEL_EDITOR_BLOCK_IDS,
  PREPACKAGED_LABEL_LAYOUT_ASSET_PLACEMENTS,
  PREPACKAGED_LABEL_LINE_DIRECTIONS,
  PREPACKAGED_LABEL_QR_ERROR_CORRECTIONS,
  PREPACKAGED_LABEL_TEXT_ALIGNS,
  PREPACKAGED_LABEL_TEXT_FONT_KEYS,
  TEXT_ASSET_COLOR_PATTERN,
  TEXT_ASSET_ID_PATTERN,
} from './prepackaged-label-editor';

/**
 * One layout change the label assistant may propose as a `LAYOUT_OPERATION`
 * suggestion card. The card is validated against this contract on read, but
 * the backend never emits it until the free-mode wave: the model output schema
 * and the sanitizer (`sanitizeLayoutOperations`) arrive with Task 16, which
 * re-validates every operation against the real document guard before it is
 * ever persisted.
 */

/** Canvas geometry in millimetres, mirroring the V2 document's block/asset geometry. */
const geometryMm = z.object({
  xMm: z.number().finite().min(-LAYOUT_OVERFLOW_MARGIN_MM),
  yMm: z.number().finite().min(-LAYOUT_OVERFLOW_MARGIN_MM),
  widthMm: z.number().finite().positive(),
  heightMm: z.number().finite().positive(),
});

/** sha256 of stored bytes (LOGO/GLYPH/CROP); generated kinds carry `''`. */
const storedContentHashSchema = z.string().regex(/^[0-9a-f]{64}$/);

/** Fields every asset carries, plus the two optional cross-kind asset-only fields. */
const assetBase = z.object({
  required: z.boolean(),
  visible: z.boolean(),
  geometry: geometryMm,
  placement: z.enum(PREPACKAGED_LABEL_LAYOUT_ASSET_PLACEMENTS).optional(),
  rotationDeg: z.number().int().min(ASSET_ROTATION_MIN_DEG).max(ASSET_ROTATION_MAX_DEG).optional(),
  provenance: z
    .object({
      source: z.literal('ASSISTANT'),
      messageId: z.string().min(1),
      appliedAt: z.string().min(1),
    })
    .optional(),
});

const storedImageAssetSchema = assetBase.extend({
  assetId: z.string().min(1),
  contentHash: storedContentHashSchema,
  kind: z.enum(['LOGO', 'GLYPH', 'CROP']),
});

/**
 * Zod mirror of the V2 layout asset contract guarded by `isLayoutAssetV2` in
 * `prepackaged-label-editor.ts` (which uses the ContractSchema pattern and
 * cannot compose into a zod discriminated union). Identity per kind follows
 * the same rules: generated TEXT carries an empty content hash, a `text-` id
 * and its own style keys; the decorative and code kinds (LINE/RECT/ELLIPSE/
 * BARCODE/QR) carry an empty content hash and a kind-namespaced id, with
 * `barcode`/`qr` required on their kind and `shape` optional with rendering
 * defaults; stored images carry a sha256 and none of them.
 */
const textAssetSchema = assetBase.extend({
  assetId: z.string().regex(TEXT_ASSET_ID_PATTERN),
  contentHash: z.literal(''),
  kind: z.literal('TEXT'),
  text: z.string().trim().min(1).max(MAX_TEXT_ASSET_LENGTH),
  fontKey: z.enum(PREPACKAGED_LABEL_TEXT_FONT_KEYS).optional(),
  bold: z.boolean().optional(),
  color: z.string().regex(TEXT_ASSET_COLOR_PATTERN).optional(),
  align: z.enum(PREPACKAGED_LABEL_TEXT_ALIGNS).optional(),
});

/** Lowercase `#rrggbb`, stroke 0.1–5 mm, `null` = no fill. */
const shapeSchema = z.object({
  strokeWidthMm: z.number().min(0.1).max(5),
  strokeColor: z.string().regex(TEXT_ASSET_COLOR_PATTERN),
  fillColor: z.string().regex(TEXT_ASSET_COLOR_PATTERN).nullable(),
});

const generatedAssetId = z.string().regex(GENERATED_ASSET_ID_PATTERN);

const lineAssetSchema = assetBase.extend({
  assetId: generatedAssetId,
  contentHash: z.literal(''),
  kind: z.literal('LINE'),
  shape: shapeSchema.optional(),
  lineDirection: z.enum(PREPACKAGED_LABEL_LINE_DIRECTIONS).optional(),
});

const rectAssetSchema = assetBase.extend({
  assetId: generatedAssetId,
  contentHash: z.literal(''),
  kind: z.literal('RECT'),
  shape: shapeSchema.optional(),
});

const ellipseAssetSchema = assetBase.extend({
  assetId: generatedAssetId,
  contentHash: z.literal(''),
  kind: z.literal('ELLIPSE'),
  shape: shapeSchema.optional(),
});

const barcodeAssetSchema = assetBase.extend({
  assetId: generatedAssetId,
  contentHash: z.literal(''),
  kind: z.literal('BARCODE'),
  barcode: z.object({
    symbology: z.enum(PREPACKAGED_LABEL_BARCODE_SYMBOLOGIES),
    source: z.enum(PREPACKAGED_LABEL_BARCODE_SOURCES),
    value: z.string().nullable(),
    humanReadable: z.boolean(),
  }),
});

const qrAssetSchema = assetBase.extend({
  assetId: generatedAssetId,
  contentHash: z.literal(''),
  kind: z.literal('QR'),
  qr: z.object({
    payload: z.string().trim().min(1).max(MAX_QR_PAYLOAD_LENGTH),
    errorCorrection: z.enum(PREPACKAGED_LABEL_QR_ERROR_CORRECTIONS),
  }),
});

export const PrepackagedLabelLayoutAssetV2Schema = z.discriminatedUnion('kind', [
  textAssetSchema,
  storedImageAssetSchema,
  lineAssetSchema,
  rectAssetSchema,
  ellipseAssetSchema,
  barcodeAssetSchema,
  qrAssetSchema,
]);
export type PrepackagedLabelLayoutAssetV2Parsed = z.infer<
  typeof PrepackagedLabelLayoutAssetV2Schema
>;

export const LayoutOperationSchema = z.discriminatedUnion('operation', [
  z.object({
    operation: z.literal('ADD_BLOCK'),
    blockId: z.enum(PREPACKAGED_LABEL_EDITOR_BLOCK_IDS),
    geometry: geometryMm.nullable(),
  }),
  // The asset travels as-is; the sanitizer (Task 16) re-derives the asset id
  // server-side before the operation is applied to the document.
  z.object({ operation: z.literal('ADD_ASSET'), asset: PrepackagedLabelLayoutAssetV2Schema }),
  z.object({
    operation: z.literal('UPDATE_ELEMENT'),
    targetId: z.string().min(1),
    geometry: geometryMm.optional(),
    rotationDeg: z.number().int().min(-180).max(180).optional(),
    text: z.string().max(200).optional(),
    visible: z.boolean().optional(),
  }),
  z.object({ operation: z.literal('REMOVE_ELEMENT'), targetId: z.string().min(1) }),
]);
export type LayoutOperation = z.infer<typeof LayoutOperationSchema>;
